import { BUCKET_COLORS, BUCKET_KEYS } from "./CityBubbleChart";

const FONT = "-apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";

const wrap = (ctx, text, maxW) => {
  const words = text.split(" "), lines = [];
  let cur = "";
  words.forEach((w) => {
    const probe = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(probe).width > maxW && cur) { lines.push(cur); cur = w; } else cur = probe;
  });
  if (cur) lines.push(cur);
  return lines;
};

const svgToImage = (svgEl, w, h) => new Promise((resolve, reject) => {
  const clone = svgEl.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", w); clone.setAttribute("height", h);
  clone.style.fontFamily = FONT;
  clone.querySelectorAll("title").forEach((n) => n.remove());
  const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
  img.src = url;
});

/** Composite: title + subtitle, the live SVG chart, colour legend, source footer. `targetWidth` fixes the output
 *  width (GIF frames must all be the same size); otherwise ≥1600px wide at 2× for the PNG. */
export async function renderChartCanvas(svgEl, { title, subtitle, legendTitle, bucketLabel, footer, dm, targetWidth }) {
  const w = svgEl.clientWidth || parseInt(svgEl.getAttribute("width"), 10);
  const h = svgEl.clientHeight || parseInt(svgEl.getAttribute("height"), 10);
  const pad = 40, headH = 120, legendH = 64, footH = 44;
  const scale = targetWidth ? targetWidth / (w + pad * 2) : Math.max(2, 1600 / w);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round((w + pad * 2) * scale);
  canvas.height = Math.round((headH + h + legendH + footH + pad) * scale);
  const ctx = canvas.getContext("2d");
  const bg = dm ? "#0F1115" : "#FFFFFF", text = dm ? "#E7E7E4" : "#111111", muted = dm ? "#A1A49F" : "#6B6F68";
  ctx.fillStyle = bg; ctx.fillRect(0, 0, canvas.width, canvas.height);   // before scaling: covers the whole bitmap
  ctx.scale(scale, scale);
  ctx.fillStyle = text; ctx.font = `700 28px Georgia, 'Times New Roman', serif`; ctx.textBaseline = "top";
  ctx.fillText(title, pad, 30);
  ctx.fillStyle = muted; ctx.font = `400 14px ${FONT}`;
  wrap(ctx, subtitle, w - 40).slice(0, 2).forEach((l, i) => ctx.fillText(l, pad, 70 + i * 19));
  const img = await svgToImage(svgEl, w, h);
  ctx.drawImage(img, pad, headH, w, h);
  let x = pad;
  ctx.font = `600 12px ${FONT}`; ctx.fillStyle = text;
  ctx.fillText(legendTitle, x, headH + h + 16);
  x += ctx.measureText(legendTitle).width + 18;
  ctx.font = `400 12px ${FONT}`;
  BUCKET_KEYS.forEach((k) => {
    ctx.fillStyle = BUCKET_COLORS[k]; ctx.beginPath(); ctx.arc(x + 6, headH + h + 22, 6, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = muted; const lbl = bucketLabel(k); ctx.fillText(lbl, x + 17, headH + h + 16);
    x += ctx.measureText(lbl).width + 36;
  });
  ctx.strokeStyle = dm ? "#2A2D34" : "#E6E6E2"; ctx.beginPath();
  ctx.moveTo(pad, headH + h + legendH); ctx.lineTo(pad + w, headH + h + legendH); ctx.stroke();
  ctx.fillStyle = muted; ctx.font = `400 11px ${FONT}`;
  ctx.fillText(footer, pad, headH + h + legendH + 14);
  return canvas;
}

export const downloadBlob = (blob, fileName) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = fileName; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

/** Social/newsletter-ready PNG (≥1600px wide). */
export async function exportChartPng(svgEl, opts) {
  const canvas = await renderChartCanvas(svgEl, opts);
  await new Promise((resolve, reject) => canvas.toBlob((blob) => {
    if (!blob) return reject(new Error("toBlob returned null"));
    downloadBlob(blob, opts.fileName);
    resolve();
  }, "image/png"));
}
